<script lang="ts">
    import type { RecordingSettings } from "@workadventure/map-editor/src/types";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { LL } from "../../../../i18n/i18n-svelte";
    import InputTags from "../../Input/InputTags.svelte";
    import InputSwitch from "../../Input/InputSwitch.svelte";
    import PureLoader from "../../PureLoader.svelte";
    import type { InputTagOption } from "../../Input/InputTagOption";
    import ButtonState from "../../Input/ButtonState.svelte";
    import { executeUpdateWAMSettings } from "../../../Phaser/Game/MapEditor/Commands/Facades";
    import { IconInfoCircle } from "@wa-icons";

    const oldRights: string[] = gameManager.getCurrentGameScene().wamFile?.settings?.recording?.rights ?? [];
    const oldEnableSounds: boolean =
        gameManager.getCurrentGameScene().wamFile?.settings?.recording?.enableSounds ?? true;
    let rights: InputTagOption[] = $state(
        oldRights.map((right) => ({ value: right, label: right.toLocaleUpperCase(), created: undefined })),
    );
    let enableSounds = $state(oldEnableSounds);
    let loading = false;
    let tagsPromise: Promise<InputTagOption[]> | undefined;

    async function getTags(): Promise<InputTagOption[]> {
        const tags = ((await gameManager.getCurrentGameScene().connection?.queryRoomTags()) ?? []).concat(
            oldRights ?? [],
        );
        return tags
            .filter((item, index) => tags.indexOf(item) === index)
            .map((tag) => ({ value: tag, label: tag.toLocaleUpperCase(), created: undefined }));
    }

    function ensureTags(): Promise<InputTagOption[]> {
        tagsPromise ??= getTags();
        return tagsPromise;
    }

    async function save(): Promise<string> {
        if (loading) {
            throw new Error("Already loading");
        }
        try {
            loading = true;
            await executeUpdateWAMSettings({
                $case: "updateRecordingSettingMessage",
                updateRecordingSettingMessage: {
                    settings: {
                        rights: (rights || []).map((right) => right.value),
                        enableSounds: enableSounds,
                    } satisfies RecordingSettings,
                },
            });
            return $LL.mapEditor.settings.recording.inputs.error.save.success();
        } catch (error) {
            console.error(error);
            throw new Error($LL.mapEditor.settings.recording.inputs.error.save.fail(), { cause: error });
        } finally {
            // eslint-disable-next-line require-atomic-updates
            loading = false;
        }
    }
</script>

<div class="flex flex-wrap gap-x-4 items-center h-fit">
    <h3 id="recording" style="color: white;">{$LL.mapEditor.settings.recording.title()}</h3>
</div>

<p class="help-text">
    <IconInfoCircle font-size="18" />
    {$LL.mapEditor.settings.recording.description()}
</p>

<div class="settings space-y-4 flex-grow flex-auto flex-shrink">
    {#await ensureTags()}
        <PureLoader size={12} color="lighter-purple" customClass="h-full" />
    {:then tags}
        <InputTags label={$LL.mapEditor.settings.recording.inputs.rights()} options={tags ?? []} bind:value={rights} />
        <p class="help-text">
            <IconInfoCircle font-size="18" />
            {$LL.mapEditor.settings.recording.inputs.rightsHelper()}
        </p>
        <div class="flex cursor-pointer items-center relative">
            <InputSwitch
                id="recording-enable-sounds"
                bind:value={enableSounds}
                label={$LL.mapEditor.settings.recording.inputs.enableSounds()}
                labelPosition="right"
            />
        </div>
        <p class="help-text">
            <IconInfoCircle font-size="18" />
            {$LL.mapEditor.settings.recording.inputs.enableSoundsHelper()}
        </p>
        <ButtonState promise={save} initialText={$LL.menu.settings.save()} loadingText="Saving" />
    {:catch error}
        <p class="help-text text-danger-800">
            <IconInfoCircle font-size="18" />
            {error}
        </p>
    {/await}
</div>
